import type { FlowDefinition, FlowFieldDefinition, FlowNode } from '../types';
import { VASE_FLOW_FIELD_IDS, rebuildFlowConnections } from './flows';
import { resolveOrCreateSectionInsertionFlow } from './sectionInsertionFlow';

const PRODUCT_DECONSTRUCTOR_FLOW_FIELDS: FlowFieldDefinition[] = [
  {
    id: VASE_FLOW_FIELD_IDS.chosenProduct,
    label: 'Chosen Product',
    type: 'entity.product',
  },
  {
    id: VASE_FLOW_FIELD_IDS.productName,
    label: 'Product Name',
    type: 'scalar.string',
  },
  {
    id: VASE_FLOW_FIELD_IDS.productPrice,
    label: 'Product Price',
    type: 'scalar.number',
  },
  {
    id: VASE_FLOW_FIELD_IDS.productMaterial,
    label: 'Product Material',
    type: 'scalar.string',
  },
];

function upsertFields(fields: FlowFieldDefinition[], additions: FlowFieldDefinition[]) {
  const nextById = new Map(fields.map((field) => [field.id, field]));
  additions.forEach((field) => {
    if (!nextById.has(field.id)) {
      nextById.set(field.id, field);
    }
  });
  return Array.from(nextById.values());
}

function upsertNodes(nodes: FlowNode[], additions: FlowNode[]) {
  const nextByBlockId = new Map(nodes.map((node) => [node.blockId, node]));
  additions.forEach((node) => {
    const existing = nextByBlockId.get(node.blockId);
    if (!existing) {
      nextByBlockId.set(node.blockId, node);
      return;
    }

    const mergeBindings = (
      current: FlowNode['inputs'] | FlowNode['outputs'],
      defaults: FlowNode['inputs'] | FlowNode['outputs']
    ) => {
      const next = [...current];
      defaults.forEach((binding) => {
        if (!next.some((entry) => entry.socketId === binding.socketId)) {
          next.push(binding);
        }
      });
      return next;
    };

    const replaceOutputBindings = (
      current: FlowNode['outputs'],
      defaults: FlowNode['outputs']
    ) => {
      const defaultSocketIds = new Set(defaults.map((binding) => binding.socketId));
      return [
        ...current.filter((binding) => !defaultSocketIds.has(binding.socketId)),
        ...defaults,
      ];
    };

    nextByBlockId.set(node.blockId, {
      ...existing,
      id: existing.id || node.id,
      blockType: node.blockType,
      config: { ...(node.config ?? {}), ...(existing.config ?? {}) },
      inputs: mergeBindings(existing.inputs, node.inputs),
      outputs: replaceOutputBindings(existing.outputs, node.outputs),
    });
  });
  return Array.from(nextByBlockId.values());
}

function buildProductDeconstructorNode(blockId: string): FlowNode {
  return {
    id: `flow-node-${blockId}`,
    blockId,
    blockType: 'product-deconstructor',
    inputs: [{ socketId: 'product', fieldId: VASE_FLOW_FIELD_IDS.chosenProduct }],
    outputs: [
      { socketId: 'name', fieldId: VASE_FLOW_FIELD_IDS.productName },
      { socketId: 'price', fieldId: VASE_FLOW_FIELD_IDS.productPrice },
      { socketId: 'material', fieldId: VASE_FLOW_FIELD_IDS.productMaterial },
    ],
  };
}

export function upsertProductDeconstructorFlowNode(
  flows: FlowDefinition[],
  args: {
    sectionId: string;
    blockId: string;
    sections?: Array<{
      id: string;
      settings?: {
        canvas?: {
          blocks?: Array<{
            id: string;
            type: string;
          }>;
        };
      };
    }> | null;
  }
) {
  const { flows: nextFlows, flow: targetFlow } = resolveOrCreateSectionInsertionFlow(flows, {
    sectionId: args.sectionId,
    sections: args.sections,
  });

  if (!targetFlow) return nextFlows;

  const updatedFlowBase: FlowDefinition = {
    ...targetFlow,
    fields: upsertFields(targetFlow.fields, PRODUCT_DECONSTRUCTOR_FLOW_FIELDS),
    nodes: upsertNodes(targetFlow.nodes, [buildProductDeconstructorNode(args.blockId)]),
  };

  const updatedFlow: FlowDefinition = {
    ...updatedFlowBase,
    connections: rebuildFlowConnections(updatedFlowBase),
  };

  return nextFlows.map((flow) => (flow.id === updatedFlow.id ? updatedFlow : flow));
}

export function upsertProductDeconstructorFlowNodesForSections(
  flows: FlowDefinition[],
  sections: Array<{
    id: string;
    settings?: {
      canvas?: {
        blocks?: Array<{
          id: string;
          type: string;
        }>;
      };
    };
  }>
) {
  return sections.reduce((nextFlows, section) => {
    const deconstructorBlocks = section.settings?.canvas?.blocks?.filter(
      (block) => block.type === 'product-deconstructor'
    );
    if (!deconstructorBlocks || deconstructorBlocks.length === 0) return nextFlows;
    return deconstructorBlocks.reduce(
      (currentFlows, block) =>
        upsertProductDeconstructorFlowNode(currentFlows, {
          sectionId: section.id,
          blockId: block.id,
          sections,
        }),
      nextFlows
    );
  }, flows);
}
